import { useEffect, useRef, useState } from "react";
import { Check, PenLine, Trash2, ArrowRight } from "lucide-react";
import type { DashboardData, ExpenseInput, Owner } from "../../types";
import ExpenseForm from "./ExpenseForm";
import { dateLabel, money, shortName, transferSummary } from "../../lib/finance";
import { batchKey, readBatch, runBatch, editBatchItem } from "./batch-save";
import type { BatchItem } from "./batch-save";
import { api } from "../../lib/api";
import { newNames } from "../../lib/names";
import LoadingLabel from "../../components/LoadingLabel";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";
import { reconciledMonths, reconciliationMonthLabel } from "../../lib/reconciliation";
export default function BatchExpenseReview({
  expenses,
  data,
  profile,
  onSaved,
  onPendingChange,
  onExpensesChange,
}: {
  expenses: ExpenseInput[];
  data: DashboardData;
  profile: Owner;
  onSaved: (accountIds?: string[]) => void;
  onPendingChange: (pending: boolean) => void;
  onExpensesChange: (expenses: ExpenseInput[]) => void;
}) {
  const [items, setItems] = useState<BatchItem[]>(
    () =>
      readBatch(profile) ||
      expenses.map((expense) => ({ expense, status: "draft" })),
  );
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [editing, setEditing] = useState<string | null>(null);
  const [started, setStarted] = useState(() => !!readBatch(profile));
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  // Categories Smart Add proposed creating. One that differs from an existing category only in case
  // is that category, not a new one: listing both would have the save refuse the pair as a duplicate.
  const entryOptions = data.entryOptions || {
    categories: [], subcategories: [], subcategoriesByCategory: {},
    incomeCategories: [], incomeSubcategories: [], incomeSubcategoriesByCategory: {},
  };
  const pendingCategories = newNames(
    entryOptions.categories,
    items.map(i => i.expense.newCategory).filter((c): c is string => !!c),
  );
  const effectiveData: DashboardData = { ...data, entryOptions: {
    ...entryOptions,
    categories: [...entryOptions.categories, ...pendingCategories],
  }};
  const mounted = useRef(true),
    running = useRef(false),
    completed = useRef(false);
  const count = items.filter((i) => i.status === "saved").length;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    onPendingChange(started || busy);
  }, [started, busy, onPendingChange]);
  useEffect(() => {
    if (items.length && count === items.length && !completed.current) {
      completed.current = true;
      // Clear the conversation first. Keep a completed journal if cleanup fails.
      try {
        localStorage.removeItem(`barnyard-smart-draft-${profile}`);
        localStorage.removeItem(batchKey(profile));
      } catch {}
      onSaved([...new Set(items.flatMap(i => [i.expense.accountId, ...(i.expense.toAccountId ? [i.expense.toAccountId] : [])]))]);
    }
  }, [items, count, onSaved, profile]);
  function persist(next: BatchItem[]) {
    localStorage.setItem(batchKey(profile), JSON.stringify(next));
    if (mounted.current) setItems(next);
  }
  function update(next: BatchItem[]) {
    if (!next.length) {
      localStorage.removeItem(batchKey(profile));
      setStarted(false);
      onPendingChange(false);
      setItems(next);
    } else if (started) persist(next);
    else setItems(next);
    onExpensesChange(next.map((i) => i.expense));
  }
  async function save() {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setError("");
    try {
      setStarted(true);
      if (pendingCategories.length) {
      // Just the category options: that is all this check compares, not the whole dashboard.
      const fresh = await api<Pick<DashboardData, "entryOptions">>("settings/categories");
      const pending = newNames(fresh.entryOptions.categories, pendingCategories);
      if (pending.length) await api("settings/categories", {
        categories: [...fresh.entryOptions.categories, ...pending],
        subcategoriesByCategory: fresh.entryOptions.subcategoriesByCategory,
        expectedOptions: fresh.entryOptions,
      });
      }
      await runBatch(
        readBatch(profile) || items,
        persist,
        fetch,
        () => mounted.current,
      );
    } catch (e) {
      if (mounted.current) setError((e as Error).message || "Save could not finish. Keep this draft and retry.");
    } finally {
      running.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  function edit(expense: ExpenseInput) {
    const latest = readBatch(profile) || items;
    try {
      update(
        latest.map((i) =>
          i.expense.requestId === editing ? editBatchItem(i, expense) : i,
        ),
      );
      setEditing(null);
    } catch (e) {
      setError((e as Error).message);
      setEditing(null);
    }
  }
  if (editing) {
    const item = items.find((i) => i.expense.requestId === editing)!;
    return (
      <div>
        <button
          type="button"
          className="text-button smart-back"
          onClick={() => setEditing(null)}
        >
          Back to list
        </button>
        <ExpenseForm
          data={effectiveData}
          profile={profile}
          initialInput={item.expense}
          onSaved={() => {}}
          onReview={edit}
        />
      </div>
    );
  }
  return (
    <div className="batch-review">
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
      <p className="batch-heading" role="status">
        {started
          ? `${count} of ${items.length} saved`
          : `Review ${items.length} expense${items.length === 1 ? "" : "s"}`}
      </p>
      <div className="batch-items">
        {items.map((item, index) => {
          const e = item.expense,
            account = (data.accounts || []).find((a) => a.id === e.accountId),
            locked =
              busy || item.status === "saved" || item.status === "pending";
          const duplicate = items
            .slice(0, index)
            .some(
              (i) =>
                i.expense.accountId === e.accountId &&
                i.expense.amount === e.amount &&
                i.expense.date === e.date &&
                i.expense.title === e.title,
            );
          return (
            <article
              className={`batch-item ${item.status}`}
              key={e.requestId}
              aria-label={`Expense ${index + 1}`}
            >
              <div className="batch-item-top">
                <span>
                  {index + 1}. {e.title}
                </span>
                <strong>{money(Number(e.amount), account?.currency)}</strong>
              </div>
              <p>
                {account?.owner} · {shortName(account?.name || "")} · {dateLabel(e.date)}
              </p>
              <p>
                {e.type || "Expense"} · {e.category}
                {e.subcategory ? ` / ${e.subcategory}` : ""}
              </p>
              {e.type === "Transfer" && <p>{transferSummary(e, data.accounts)}</p>}
              {e.payeeName && <p>Payee: {e.payeeName}{!(data.payees || []).some(p => p.name.toLowerCase() === e.payeeName!.toLowerCase()) ? " (new)" : ""}</p>}
              {e.reportableAmount !== undefined && Number(e.reportableAmount) !== Number(e.amount) && <p>Household {e.type === "Income" ? "income" : "spending"}: {money(Number(e.reportableAmount), account?.currency)}</p>}
              {e.notes && <p className="batch-note">{e.notes}</p>}
              {duplicate && item.status === "draft" && (
                <p className="batch-warning">
                  Same title, amount, date and account as an earlier
                  item—check this is a separate purchase.
                </p>
              )}
              {item.error && (
                <p className="form-error" role="alert">
                  {item.error}
                </p>
              )}
              <div className="batch-item-actions">
                <span>
                  {item.status === "saved" ? (
                    <>
                      <Check size={15} />
                      Saved & verified
                    </>
                  ) : item.status === "pending" ? (
                    "Awaiting confirmation"
                  ) : item.status === "error" ? (
                    "Not saved"
                  ) : (
                    "Not saved yet"
                  )}
                </span>
                {!locked && (
                  <>
                    <button
                      type="button"
                      aria-label={`Edit expense ${index + 1}`}
                      onClick={() => setEditing(e.requestId)}
                    >
                      <PenLine size={16} />
                      Edit
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove expense ${index + 1}`}
                      onClick={() => {
                        const latest = readBatch(profile) || items;
                        if (
                          latest.find(
                            (i) => i.expense.requestId === e.requestId,
                          )?.status === "pending"
                        )
                          return;
                        update(
                          latest.filter(
                            (i) => i.expense.requestId !== e.requestId,
                          ),
                        );
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            </article>
          );
        })}
      </div>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {count < items.length && (
        <button
          type="button"
          className="primary-button batch-save-all"
          disabled={busy}
          onClick={() => {
            const months = reconciledMonths(data, items.filter((item) => item.status !== "saved").map((item) => item.expense.date));
            if (months.length) setConfirm({ title: "Reopen a reconciled month?", message: `This batch affects ${months.map(reconciliationMonthLabel).join(", ")}. Saving it will reopen the affected balance reviews.`, confirmLabel: "Save anyway", onConfirm: save });
            else void save();
          }}
        >
          {busy
            ? <LoadingLabel>Saving</LoadingLabel>
            : started
              ? "Check & save remaining"
              : `Save all ${items.length} expense${items.length === 1 ? "" : "s"}`}
          <ArrowRight size={18} />
        </button>
      )}
      {!started && (
        <p className="smart-caption">
          Each expense is saved separately. Review the list before
          saving.
        </p>
      )}
    </div>
  );
}
